import React from 'react';
import RenderItem from './RenderItem'
import PhotographerPage from './PhotographerPage';
import axios from 'axios';
import { baseURL } from '../../api/baseURL';
import { getSnapwizMediaOrigin } from '../../api/hostport';
import { mergeSpecializationOptionsFromMisc } from '../../constants/photographerSpecializations';
import Loader from '../../Loader';
import Modal from '../../Modal';
import { connect } from 'react-redux';
import { MDBDropdown, MDBDropdownMenu, MDBDropdownToggle, MDBDropdownItem, MDBInput, MDBBtn } from 'mdb-react-ui-kit';
import Pagination from '../Pagination';
import debounce from 'debounce';
import ExportButton from '../ExportButton';
import { isMainAdmin } from '../../utils/isMainAdmin';
import AdminShell from '../layout/AdminShell';
import { pillBarStyle, pillStyle, toolbarRowStyle, searchBoxWrapperStyle, thStyle } from '../layout/listUi';

type State = {
    requested: boolean,
    err: any,
    photographersList: any,
    searchedList: any,
    photoOpen: boolean,
    modalOpen: boolean,
    currentPage: number,
    successPayout: boolean,
    activeTab: 'all' | 'pending',
    pendingList: any,
    pendingPage: number,
};

class PhotographerList extends React.Component<{ history: any, token: string, role?: string }> {
    state: State = {
        requested: false,
        err: false,
        successPayout: false,
        photographersList: null,
        searchedList: null,
        photoOpen: false,
        modalOpen: false,
        currentPage: 1,
        activeTab: 'all',
        pendingList: null,
        pendingPage: 1,
    }

    getMiscDoc = (): any => {
        const m = this.state.photographersList?.miscData ?? this.state.pendingList?.miscData ?? this.state.searchedList?.miscData;
        if (m && typeof m === 'object' && !Array.isArray(m)) {
            return m;
        }
        return null;
    };

    /** Active MiscData `specialization_name` values ONLY — client requirement:
     * a service should show here (and in registration/verify) exactly when
     * it's present in Admin → Services, nothing more. Previously this also
     * injected whatever was already on the photographer's own record, so a
     * stale/removed service (e.g. an old test entry) kept reappearing as a
     * selectable option here forever, which is exactly what was reported. */
    specializationOptionsFor = () =>
        mergeSpecializationOptionsFromMisc(this.getMiscDoc());

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = "/";
        }
        else {
            this.getPhotographersList(parseInt(window.location.search.split("?page=")[1]) ? parseInt(window.location.search.split("?page=")[1]) : 1);
            this.getPendingList(1);

            // Client-reported: Pending Requests page's "Review on Photographers
            // page" link only landed on the generic list — open that specific
            // photographer's own detail modal directly instead.
            const params = new URLSearchParams(window.location.search);
            const photographerId = params.get('photographerId');
            if (photographerId) this.openPhotographerById(photographerId);
        }
    }

    openPhotographerById = (photographerId: string) => {
        axios.get(`${baseURL}/photographers?photographerId=${photographerId}`, {
            headers: { Authorization: `Bearer ${this.props.token}` }
        })
            .then((response: any) => {
                const found = response?.data?.photographers?.[0];
                if (found) this.toggleOpen(found);
            })
            .catch(() => {});
    }

    getPendingList = (pageNumber: number) => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });
            axios.get(`${baseURL}/photographers?page=${pageNumber}&limit=10&pending=true`,
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                    if (response?.data?.message === "success") {
                        this.setState({ pendingList: response?.data, requested: false, err: false, pendingPage: pageNumber })
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                    this.setState({ err: true, requested: false });
                })
        }
    }

    assignSpecialization = (userId: string, specialization: string[]) => {
        if (!userId || !Array.isArray(specialization) || this.state.requested) {
            return;
        }
        this.setState({ err: false, requested: true });
        axios.post(`${baseURL}/assign-specialization`,
            { userId, specialization },
            {
                headers: { Authorization: `Bearer ${this.props.token}` }
            })
                .then((response: any) => {
                    if (response?.data?.message === "success") {
                        this.setState({ requested: false, err: false });
                        this.getPhotographersList(this.state.currentPage);
                        this.getPendingList(this.state.pendingPage);
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                    this.setState({ err: true, requested: false });
                });
    }

    handleSpecializationSavedFromModal = (patch: { specialization: string[]; is_admin_verified?: boolean }) => {
        this.setState((prev: State) => {
            const m = prev.modalOpen;
            if (!m || typeof m !== 'object') {
                return { requested: false, err: false } as any;
            }
            return {
                modalOpen: { ...m, ...patch },
                requested: false,
                err: false,
            };
        });
        this.getPhotographersList(this.state.currentPage);
        this.getPendingList(this.state.pendingPage);
    }

    getPhotographersList = (pageNumber: number) => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });
            axios.get(`${baseURL}/photographers?page=${pageNumber}&limit=10`,
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                  //console.log(response);
                    if (response?.data?.message === "success") {
                        this.setState({ photographersList: response?.data, requested: false, err: false, currentPage: pageNumber })
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                  //console.log(error);
                    this.setState({ err: true, requested: false });
                })
        }
    }

    onPageChange = (pageNumber: number) => {
        this.getPhotographersList(pageNumber);
    }

    toggleOpen = (photographerData: any) => {
      //console.log(photographerData, this.state.modalOpen)
        if (this.state.modalOpen) {
            this.setState({ modalOpen: false });
        }
        else if (!this.state.modalOpen) {
            this.setState({ modalOpen: photographerData });
        }
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }

        if (this.state.successPayout) {
            this.setState({ successPayout: false });
        }
    }

    getSearchList = (searchText: string) => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });
            axios.get(`${baseURL}/photographers?search=${searchText}&limit=10`,
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                  //console.log(response);
                    if (response?.data?.message === "success") {
                        this.setState({ searchedList: response?.data, requested: false, err: false });
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                  //console.log(error);
                    this.setState({ err: true, requested: false });
                })
        }
    }

    onSearch = debounce((searchText: string) => {
        if (searchText.length > 2) {
            this.getSearchList(searchText);
        }
    }, 100);

    onClickClear = () => {
        let searchInputEl: any = document.getElementById("search_input");
        if (searchInputEl?.value) {
            searchInputEl.value = "";
        }

        if (this.state.searchedList) {
          //console.log("cleared")
            this.setState({ searchedList: null });
        }
    }

    updateVerifyStatus = (userId: string) => {
        if (userId && !this.state.requested) {
            this.setState({ err: false, requested: true });
            axios.post(`${baseURL}/update-verify-status`,
                {
                    userId
                },
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                  //console.log(response);
                    if (response?.data?.message === "success") {
                        for (let i = 0; i < this.state.photographersList.photographers.length; i++) {
                            if (this.state.photographersList.photographers[i]?._id === userId) {
                                let temp = this.state.photographersList;
                                temp.photographers[i].is_admin_verified = !temp.photographers[i].is_admin_verified;
                                this.setState({ requested: false, err: false, photographersList: temp });
                                break;
                            }
                        }
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                  //console.log(error);
                    const msg = error?.response?.data?.message;
                    if (msg === "assign_services_first") {
                        window.alert(error?.response?.data?.detail || "Assign services on Pending Assignment before verifying.");
                    }
                    else if (msg === "otp_not_completed") {
                        window.alert(error?.response?.data?.detail || "This photographer has not completed OTP verification yet — cannot verify.");
                    }
                    this.setState({ err: true, requested: false });
                })
        }
    }

    render() {
        return (
            <AdminShell activePage="photographers" title="Photographers" subtitle="Verification, assignment, availability, and payouts" topbarActions={<ExportButton endpoint="/export/photographers" />}>
                <Modal open={this.state.err || this.state.successPayout} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>{this.state.successPayout ? "Payment sent successfully" : this.state.err === "no_bank_details" ? "There is no bank details added" : "Something went wrong, please try again later"}</h6>} />
                {this.state.requested ? <Loader /> : null}
                            <div className="row ">
                                <div className="col-12 grid-margin">
                                    <div className="card">
                                        <div className="card-body">
                                            <div style={pillBarStyle}>
                                                <span
                                                    style={pillStyle(this.state.activeTab === 'all')}
                                                    onClick={() => this.setState({ activeTab: 'all' })}
                                                >
                                                    All Photographers {this.state.photographersList?.total ? `(${this.state.photographersList?.total})` : ""}
                                                </span>
                                                <span
                                                    style={pillStyle(this.state.activeTab === 'pending')}
                                                    onClick={() => this.setState({ activeTab: 'pending' })}
                                                >
                                                    Pending Assignment {this.state.pendingList?.total ? `(${this.state.pendingList?.total})` : "(0)"}
                                                </span>
                                            </div>

                                            {this.state.activeTab === 'all' ? <>
                                            <div style={toolbarRowStyle}>
                                                <div style={searchBoxWrapperStyle}>
                                                    <MDBInput
                                                        onChange={(e) => {
                                                            this.onSearch(e.target.value)
                                                        }}
                                                        contrast label="Search by Name / Email" id="search_input" type="text" style={{ border: 'none' }} />
                                                </div>
                                                <MDBBtn onClick={() => { this.onClickClear() }} style={{ width: '200px' }} color='secondary' rippleColor='light'>Clear</MDBBtn>
                                            </div>
                                            <div className="table-responsive">
                                                {(this.state.searchedList?.photographers?.length || this.state.photographersList?.photographers?.length) ? <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th style={thStyle}> Name </th>
                                                            <th style={thStyle}> Email </th>
                                                            <th style={thStyle}> Contact </th>
                                                            <th style={thStyle}> Action </th>
                                                            <th style={thStyle}>Is Assigned</th>
                                                            <th style={thStyle}> Address </th>
                                                            <th style={thStyle}> Availability </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.searchedList?.photographers?.length ?
                                                            this.state.searchedList?.photographers.map((item: any) => {
                                                                return <RenderItem token={this.props.token} specializationOptions={this.specializationOptionsFor()} requested={this.state.requested} err={this.state.err || this.state.successPayout} toggleOpen={this.toggleOpen} item={item} updateVerifyStatus={this.updateVerifyStatus} isMainAdmin={isMainAdmin(this.props.role)} />;
                                                            }) : this.state.photographersList?.photographers?.length ?
                                                                this.state.photographersList?.photographers.map((item: any) => {
                                                                    return <RenderItem token={this.props.token} specializationOptions={this.specializationOptionsFor()} requested={this.state.requested} err={this.state.err || this.state.successPayout} toggleOpen={this.toggleOpen} item={item} updateVerifyStatus={this.updateVerifyStatus} isMainAdmin={isMainAdmin(this.props.role)} />;
                                                                })
                                                                : null}
                                                    </tbody>
                                                </table> : <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                                    <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                                                </div>}
                                            </div>
                                            </> : <>
                                            <div className="table-responsive">
                                                {this.state.pendingList?.photographers?.length ? <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th style={thStyle}> Name </th>
                                                            <th style={thStyle}> Email </th>
                                                            <th style={thStyle}> Contact </th>
                                                            <th style={thStyle}> Requested Services </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.pendingList?.photographers.map((item: any) => {
                                                            return <RenderItem key={item._id} specializationOptions={this.specializationOptionsFor()} isPending={true} assignSpecialization={this.assignSpecialization} requested={this.state.requested} err={this.state.err || this.state.successPayout} toggleOpen={this.toggleOpen} item={item} updateVerifyStatus={this.updateVerifyStatus} isMainAdmin={isMainAdmin(this.props.role)} />;
                                                        })}
                                                    </tbody>
                                                </table> : <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                                    <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No pending photographers</div>
                                                </div>}
                                            </div>
                                            </>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {this.state.activeTab === 'all' ?
                            ((this.state.photographersList?.total / 10) > 1 ? <Pagination onPageChange={this.onPageChange} currentPage={this.state.currentPage} totalPages={(this.state.photographersList?.total / 10) < 1 ? (this.state.photographersList?.total / 10) : Math.ceil(this.state.photographersList?.total / 10)} maxVisible={5} /> : null)
                            : ((this.state.pendingList?.total / 10) > 1 ? <Pagination onPageChange={(page: number) => this.getPendingList(page)} currentPage={this.state.pendingPage} totalPages={Math.ceil(this.state.pendingList?.total / 10)} maxVisible={5} /> : null)
                            }
                <PhotographerPage
                    open={this.state.modalOpen}
                    toggleOpen={this.toggleOpen}
                    token={this.props.token}
                    mediaOrigin={getSnapwizMediaOrigin()}
                    specializationOptions={this.specializationOptionsFor()}
                    onSpecializationSaved={this.handleSpecializationSavedFromModal}
                    onToggleVerify={this.updateVerifyStatus}
                    verifyBusy={this.state.requested}
                    isMainAdmin={isMainAdmin(this.props.role)}
                />
            </AdminShell>
        );
    }
}


const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token,
        role: state.auth.role,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PhotographerList);
